import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { motion } from "framer-motion";
import { Plus, Trash2, Shield, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const ROLES = [
  { value: "admin", label: "Admin", desc: "Full access — user management, settings, all data", color: "bg-red-100 text-red-700" },
  { value: "agent", label: "Agent", desc: "Lead management, campaigns, content editing", color: "bg-blue-100 text-blue-700" },
  { value: "assistant", label: "Assistant", desc: "Read-only access to leads and dashboard", color: "bg-slate-100 text-slate-700" },
];

const UsersPage = () => {
  const { token, user: currentUser } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent" });
  const [creating, setCreating] = useState(false);

  const isAdmin = currentUser?.role === "admin";

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast({ title: "Access denied", description: "Only admins can manage users", variant: "destructive" });
      }
    }
    setLoading(false);
  }, [token, toast]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "User created", description: `${form.name} (${form.role}) added` });
      setForm({ name: "", email: "", password: "", role: "agent" });
      setShowCreate(false);
      fetchUsers();
    } catch (err) {
      toast({ title: "Failed", description: err.response?.data?.detail || "Error creating user", variant: "destructive" });
    }
    setCreating(false);
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "User deleted" });
      fetchUsers();
    } catch (err) {
      toast({ title: "Failed", description: err.response?.data?.detail || "Error", variant: "destructive" });
    }
  };

  const updateRole = async (id, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${id}/role`, { role: newRole }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Role updated" });
      fetchUsers();
    } catch (err) {
      toast({ title: "Failed", description: err.response?.data?.detail || "Error", variant: "destructive" });
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#0A2342]">Access Restricted</h2>
          <p className="text-slate-500 mt-2">Only administrators can manage users</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']" data-testid="users-title">User Management</h1>
          <p className="text-slate-600">Manage team access and permissions</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center" data-testid="create-user-btn">
          <Plus className="w-5 h-5 mr-2" /> Add User
        </button>
      </div>

      {/* Role Legend */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-[#0A2342] mb-3">Role Permissions</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {ROLES.map(r => (
            <div key={r.value} className="flex items-start gap-3">
              <span className={`px-2 py-1 rounded text-xs font-bold ${r.color}`}>{r.label}</span>
              <p className="text-sm text-slate-500">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-xl border border-slate-200" data-testid="users-list">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-[#0A2342]">Team Members ({users.length})</h2>
        </div>
        {loading ? (
          <div className="p-12 text-center"><span className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2342] inline-block" /></div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No users found</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {users.map(u => {
              const roleInfo = ROLES.find(r => r.value === u.role) || ROLES[2];
              const isSelf = u.id === currentUser?.id;
              return (
                <div key={u.id} className="p-4 px-6 flex items-center justify-between hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#0A2342] rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {u.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <p className="font-medium text-[#0A2342]">
                        {u.name} {isSelf && <span className="text-xs text-slate-400">(you)</span>}
                      </p>
                      <p className="text-sm text-slate-500">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isSelf ? (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${roleInfo.color}`}>{roleInfo.label}</span>
                    ) : (
                      <select
                        value={u.role || "assistant"}
                        onChange={(e) => updateRole(u.id, e.target.value)}
                        className="text-sm border rounded-lg px-3 py-1 text-slate-700"
                        data-testid={`user-role-select-${u.id}`}
                      >
                        {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    )}
                    {!isSelf && (
                      <button onClick={() => deleteUser(u.id, u.name)} className="p-2 text-slate-400 hover:text-red-500 transition-colors" data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-md shadow-2xl" data-testid="create-user-modal">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#0A2342]">Add Team Member</h3>
              <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={createUser} className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Full Name</label>
                <input type="text" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  className="input-field" placeholder="Jane Smith" data-testid="new-user-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Email</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  className="input-field" placeholder="jane@urbannestquest.com" data-testid="new-user-email" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Password</label>
                <input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                  className="input-field" placeholder="Min 8 characters" data-testid="new-user-password" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Role</label>
                <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="input-field" data-testid="new-user-role">
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 btn-primary" data-testid="submit-new-user">
                  {creating ? <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block" /> : "Create User"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
